import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../hooks/useFocusTrap';
import styles from './ModalDialog.module.css';

export interface ModalDialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * A modal dialog titled `title`: focus moves in and is trapped, Escape and a
 * click on the backdrop close it, and focus returns when it's gone. Render it
 * only while it's open.
 *
 * It renders at the end of `<body>`, not where it's written: a dialog opened
 * from inside a form (adding a film stock while booking in) would otherwise
 * put its form inside that one, and a browser then submits the page instead.
 * React events still bubble to the component that opened it, so a form in
 * a dialog stops its own submit (see `FilmStockForm`).
 */
export function ModalDialog({ title, onClose, children }: ModalDialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, onClose);
  return createPortal(
    // Only a click on the backdrop itself closes; Escape is the focus trap's.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titleId} className={styles.dialog} tabIndex={-1}>
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body,
  );
}
